import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceLinkContract,
  CustomerFieldBindingContract,
} from '@/contracts/customer-application-binding'
import type { CustomerFieldMappingContract, DuplicateResolutionMode } from '@/contracts/customers'
import type {
  FormQuestionGroupInstanceContract,
  QuestionGroupTemplateContract,
  QuestionOutlineItem,
} from '@/contracts/question-groups'
import { type FormFolderContract, createSyntheticMeta } from '@/contracts/questionnaire'
import type { QuestionGroupUiContract } from '@/contracts/questionnaire'
import {
  DEFAULT_SHARE_MAX_RESPONSES,
  type EmailShareTemplateContract,
  type ShareSettingsContract,
  type TrackingLinkContract,
} from '@/contracts/share'
import {
  type CustomerApplication,
  type ManagedCustomer,
  type SalesUser,
  initialApplications,
  initialSalesUsers,
  normalizeSalesUsersForInternalSeed,
} from '@/lib/business-workflow'
import { createFieldBindingsFromMappings } from '@/lib/customer-application-binding'
import { createMiaolinCustomerFieldMappings } from '@/lib/miaolin-customer-spec'
import { createDefaultQuestionGroupTemplates } from '@/lib/mock-api/question-group-fixtures'
import {
  getSampleForm,
  miaolinQuestionGroupDefinitions,
  mockWorkspaceId,
  primaryFormId,
  sampleForms,
} from '@/lib/mock-data'
import { buildPublicFormUrl } from '@/lib/public-form-url'
import type { Form, LogicJump, Question, ResponseSummary } from '@/types/form'

export const PERSISTENT_MOCK_STORAGE_KEY = 'miaoform.mock.v4'
export const PERSISTENT_MOCK_DEMO_USER_KEY = 'miaoform_demo_user'

export type BusinessWorkflowSnapshot = {
  viewMode: 'admin' | 'manager' | 'sales' | 'assistant'
  salesUsers: SalesUser[]
  applications: CustomerApplication[]
  customers: ManagedCustomer[]
  fieldMappings: CustomerFieldMappingContract[]
  fieldBindings: CustomerFieldBindingContract[]
  sourceLinks: CustomerApplicationSourceLinkContract[]
  fieldValues: Record<string, CustomerApplicationFieldValueContract[]>
  duplicateDecisions: Record<string, DuplicateResolutionMode>
}

export type PersistentFormDetailSnapshot = {
  questions: Question[]
  logicJumps: LogicJump[]
  hiddenFields: Record<string, string>
  groups: QuestionGroupUiContract[]
  outline: QuestionOutlineItem[]
}

export type PersistentMockState = {
  version: 4
  updatedAt: string
  sourceMeta: ReturnType<typeof createSyntheticMeta>
  forms: Form[]
  formFolders: FormFolderContract[]
  formOrder: string[]
  deletedFormIds: string[]
  responses: ResponseSummary[]
  formDetails: Record<string, PersistentFormDetailSnapshot>
  questionGroupTemplates: QuestionGroupTemplateContract[]
  questionGroupInstances: Record<string, FormQuestionGroupInstanceContract[]>
  questionOutlineOrder: Record<string, QuestionOutlineItem[]>
  shareSettings: Record<string, ShareSettingsContract>
  trackingLinks: Record<string, TrackingLinkContract[]>
  emailShareTemplates: Record<string, EmailShareTemplateContract[]>
  businessWorkflow: BusinessWorkflowSnapshot
}

const defaultFieldMappings: CustomerFieldMappingContract[] =
  createMiaolinCustomerFieldMappings(primaryFormId)

const legacyCustomerMockResponseIds = new Set([
  '77777777-7777-4777-8777-777777777771',
  '77777777-7777-4777-8777-777777777772',
  '77777777-7777-4777-8777-777777777773',
  '77777777-7777-4777-8777-777777777774',
  '77777777-7777-4777-8777-777777777775',
])

const legacyCustomerMockApplicationIds = new Set([
  'app-apex',
  'app-nova',
  'app-orbit',
  'app-nova-branch',
])

const legacyCustomerMockCustomerIds = new Set([
  'cus-app-nova',
  'cus-preview-filed-zz3222',
  'cus-preview-filed-tt454',
  'cus-preview-filed-tn452',
  'cus-preview-filed-88999',
  'cus-preview-filed-kmdldop',
])

const legacyCustomerMockSourceLinkIds = new Set(['source-link-app-apex'])

let memoryState: PersistentMockState | null = null

type CustomerMockScope = 'preview' | 'production'

function getCustomerMockScope(): CustomerMockScope {
  const explicitScope = import.meta.env.VITE_MIAOFORM_CUSTOMER_MOCK_SCOPE
  if (explicitScope === 'preview') return 'preview'
  if (explicitScope === 'production' || explicitScope === 'disabled') return 'production'

  const appUrl = import.meta.env.VITE_APP_URL ?? ''
  if (/preview|localhost|127\.0\.0\.1|\[::1\]/i.test(appUrl)) return 'preview'
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') return 'preview'
  return 'production'
}

export function isPreviewCustomerMockDataEnabled() {
  return getCustomerMockScope() === 'preview'
}

function clone<T>(value: T): T {
  try {
    return structuredClone(value)
  } catch {
    return JSON.parse(
      JSON.stringify(value, (_key, nestedValue) =>
        typeof nestedValue === 'function' ? undefined : nestedValue,
      ),
    ) as T
  }
}

function getStorage() {
  try {
    return globalThis.localStorage
  } catch {
    return null
  }
}

export function getPersistentMockStorageKey() {
  const storage = getStorage()
  if (!storage) return PERSISTENT_MOCK_STORAGE_KEY
  try {
    const rawUser = storage.getItem(PERSISTENT_MOCK_DEMO_USER_KEY)
    if (!rawUser) return PERSISTENT_MOCK_STORAGE_KEY
    const user = JSON.parse(rawUser) as { email?: string; userId?: string }
    const userKey = (user.email ?? user.userId ?? '')
      .trim()
      .toLocaleLowerCase()
      .replace(/[^a-z0-9._-]+/g, '_')
    return userKey ? `${PERSISTENT_MOCK_STORAGE_KEY}.${userKey}` : PERSISTENT_MOCK_STORAGE_KEY
  } catch {
    return PERSISTENT_MOCK_STORAGE_KEY
  }
}

function getBaseUrl(formId: string) {
  return buildPublicFormUrl(formId)
}

function createInitialFolders(): FormFolderContract[] {
  return [
    {
      id: 'folder-all-customer',
      workspaceId: mockWorkspaceId,
      name: '客戶建檔',
      color: '#0F766E',
      orderIndex: 0,
      formIds: [sampleForms[0].id],
    },
  ]
}

function createMiaolinDemoGroups(formId: string, questions: Question[]): QuestionGroupUiContract[] {
  return miaolinQuestionGroupDefinitions
    .map((definition) => {
      const groupQuestions = questions.filter((question) => question.groupId === definition.id)
      return {
        id: definition.id,
        formId,
        sourceGroupId: definition.id,
        mode: 'copy' as const,
        title: definition.title,
        description: definition.description,
        orderIndex: definition.orderIndex,
        collapsed: false,
        questionIds: groupQuestions.map((question) => question.id),
        questions: groupQuestions,
        logicJumps: [],
        sourceMeta: createSyntheticMeta('2026-05-26'),
      }
    })
    .filter((group) => group.questionIds.length > 0)
}

function createInitialFormDetails(): Record<string, PersistentFormDetailSnapshot> {
  return Object.fromEntries(
    sampleForms.map((form) => {
      const publicForm = getSampleForm(form.id)
      const groups = createMiaolinDemoGroups(form.id, publicForm.questions)
      return [
        form.id,
        {
          questions: publicForm.questions,
          logicJumps: publicForm.logicJumps,
          hiddenFields: publicForm.hiddenFields,
          groups,
          outline: createQuestionOutline(
            publicForm.questions,
            normalizeFormGroupInstances(form.id, groups),
          ),
        },
      ]
    }),
  )
}

function createShareSettings(form: Form): ShareSettingsContract {
  const publicUrl = getBaseUrl(form.id)
  return {
    formId: form.id,
    publicUrl,
    isEnabled: form.status === 'published',
    closeAt: typeof form.settings.closedAt === 'string' ? form.settings.closedAt : null,
    maxResponses:
      typeof form.settings.maxResponses === 'number'
        ? form.settings.maxResponses
        : DEFAULT_SHARE_MAX_RESPONSES,
    allowRepeat: form.settings.allowResubmit === true,
    defaultSalesCode: 'ARIEL',
    defaultCampaignCode: 'Q2-NURTURE',
    hiddenFields: {
      salesCode: 'ARIEL',
      campaignCode: 'Q2-NURTURE',
    },
    qrCodeUrl: `${publicUrl}?qr=1`,
    sourceMeta: createSyntheticMeta('2026-06-01'),
  }
}

function createInitialShareSettings(forms = sampleForms): Record<string, ShareSettingsContract> {
  return Object.fromEntries(forms.map((form) => [form.id, createShareSettings(form)]))
}

function createEmailShareTemplates(form: Form): EmailShareTemplateContract[] {
  const variables = [
    '{{form.title}}',
    '{{form.url}}',
    '{{tracking.url}}',
    '{{workspace.name}}',
    '{{sender.name}}',
    '{{sender.email}}',
    '{{sales.name}}',
    '{{sales.email}}',
    '{{campaign.code}}',
    '{{recipient.email}}',
    '{{deadline}}',
  ]
  return [
    {
      id: `${form.id}-email-template-general`,
      formId: form.id,
      name: '一般問卷邀請',
      category: 'general_invitation',
      description: '適合寄送一般滿意度、需求調查或內部問卷。',
      subject: '邀請您填寫 {{form.title}}',
      body: '您好，\n\n我們想邀請您填寫「{{form.title}}」。\n請使用以下連結開始填寫：\n{{tracking.url}}\n\n謝謝您。\n{{workspace.name}}',
      defaultCcStrategy: 'none',
      defaultCcSalesUserIds: [],
      variables,
      updatedAt: '2026-05-22T00:00:00.000Z',
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
    {
      id: `${form.id}-email-template-b2b`,
      formId: form.id,
      name: 'B2B 新客建檔邀請',
      category: 'b2b_customer_onboarding',
      description: '適合請業務或客戶補齊新客戶建檔資料。',
      subject: '請協助填寫 {{form.title}}',
      body: '{{sales.name}} 您好，\n\n請協助填寫「{{form.title}}」，以便完成新客戶建檔申請。\n問卷連結：{{tracking.url}}\n\n若資料需由客戶補充，可轉寄此連結並保留 CC 業務窗口。\n{{workspace.name}}',
      defaultCcStrategy: 'selected_sales_users',
      defaultCcSalesUserIds: ['sales1'],
      variables,
      updatedAt: '2026-05-22T00:00:00.000Z',
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
    {
      id: `${form.id}-email-template-event`,
      formId: form.id,
      name: '活動回饋問卷',
      category: 'event_feedback',
      description: '適合活動、研討會、課程結束後寄送回饋連結。',
      subject: '感謝參與，請填寫 {{form.title}}',
      body: '您好，\n\n感謝您參與本次活動。請花 2 分鐘填寫「{{form.title}}」，讓我們持續改善服務。\n{{tracking.url}}\n\n期待下次再見。\n{{workspace.name}}',
      defaultCcStrategy: 'none',
      defaultCcSalesUserIds: [],
      variables,
      updatedAt: '2026-05-22T00:00:00.000Z',
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
    {
      id: `${form.id}-email-template-sales-followup`,
      formId: form.id,
      name: '業務跟進問卷',
      category: 'sales_followup',
      description: '適合業務跟進商機、需求確認或滿意度回訪。',
      subject: '{{sales.name}} 邀請您填寫 {{form.title}}',
      body: '您好，\n\n我是 {{sales.name}}。想請您協助填寫「{{form.title}}」，讓我們更準確理解需求並安排後續服務。\n{{tracking.url}}\n\n如有問題可直接回覆此信。\n{{sales.email}}',
      defaultCcStrategy: 'form_sales_owner',
      defaultCcSalesUserIds: ['sales1'],
      variables,
      updatedAt: '2026-05-22T00:00:00.000Z',
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
  ]
}

function createInitialEmailShareTemplates(
  forms = sampleForms,
): Record<string, EmailShareTemplateContract[]> {
  return Object.fromEntries(forms.map((form) => [form.id, createEmailShareTemplates(form)]))
}

export function createDefaultBusinessWorkflowSnapshot(): BusinessWorkflowSnapshot {
  return {
    viewMode: 'admin',
    salesUsers: initialSalesUsers,
    applications: initialApplications,
    customers: [],
    fieldMappings: defaultFieldMappings,
    fieldBindings: createFieldBindingsFromMappings(
      defaultFieldMappings,
      '2026-05-22T00:00:00.000Z',
    ),
    sourceLinks: createDefaultSourceLinks(),
    fieldValues: {},
    duplicateDecisions: {},
  }
}

function createDefaultSourceLinks(): CustomerApplicationSourceLinkContract[] {
  return []
}

function mergeById<T extends { id: string }>(preferred: T[], additions: T[]): T[] {
  return [
    ...preferred,
    ...additions.filter((item) => !preferred.some((preferredItem) => preferredItem.id === item.id)),
  ]
}

const currentPhoneLabels = {
  phone1: {
    questionId: 'q-miaolin-10',
    questionTitle: '市話',
    targetLabel: '市話',
    targetFullLabel: '聯絡資訊 / 市話',
  },
  phone2: {
    questionId: 'q-miaolin-11',
    questionTitle: '手機',
    targetLabel: '手機',
    targetFullLabel: '聯絡資訊 / 手機',
  },
} as const

function refreshMiaolinPhoneFieldMappings(mappings: CustomerFieldMappingContract[]) {
  return mappings.map((mapping) => {
    const phone = currentPhoneLabels[mapping.targetField as keyof typeof currentPhoneLabels]
    if (!phone) return mapping
    return {
      ...mapping,
      questionTitle:
        mapping.questionId === phone.questionId ? phone.questionTitle : mapping.questionTitle,
      targetLabel: phone.targetLabel,
    }
  })
}

function refreshMiaolinPhoneFieldBindings(bindings: CustomerFieldBindingContract[]) {
  return bindings.map((binding) => {
    const phone = currentPhoneLabels[binding.targetField as keyof typeof currentPhoneLabels]
    if (!phone) return binding
    return {
      ...binding,
      questionTitle:
        binding.questionId === phone.questionId ? phone.questionTitle : binding.questionTitle,
      targetLabel: phone.targetLabel,
      targetFullLabel: phone.targetFullLabel,
    }
  })
}

function refreshMiaolinPhoneFieldValues(
  values: Record<string, CustomerApplicationFieldValueContract[]>,
) {
  return Object.fromEntries(
    Object.entries(values).map(([applicationId, fieldValues]) => [
      applicationId,
      fieldValues.map((fieldValue) => {
        const phone = currentPhoneLabels[fieldValue.fieldKey as keyof typeof currentPhoneLabels]
        if (!phone) return fieldValue
        return {
          ...fieldValue,
          label: phone.targetLabel,
          targetLabel: phone.targetFullLabel,
          sourceQuestionTitle:
            fieldValue.sourceQuestionId === phone.questionId
              ? phone.questionTitle
              : fieldValue.sourceQuestionTitle,
        }
      }),
    ]),
  )
}

export function normalizeFormGroupInstances(
  formId: string,
  groups: QuestionGroupUiContract[],
): FormQuestionGroupInstanceContract[] {
  return groups.map((group, orderIndex) => {
    const mode = group.mode ?? 'copy'
    return {
      id: group.id,
      formId,
      sourceGroupId: group.sourceGroupId ?? group.id,
      mode,
      title: group.title,
      description: group.description,
      orderIndex: group.orderIndex ?? orderIndex,
      collapsed: group.collapsed,
      questionIds: group.questionIds,
      questions: group.questions,
      logicJumps: group.logicJumps,
      lockedReason:
        group.lockedReason ?? (mode === 'synced' ? '同步題組需回到題組管理編輯。' : undefined),
      sourceMeta: group.sourceMeta ?? createSyntheticMeta('2026-05-21'),
    }
  })
}

export function createQuestionOutline(
  questions: Pick<Question, 'id' | 'groupId' | 'orderIndex'>[],
  groups: Pick<FormQuestionGroupInstanceContract, 'id' | 'orderIndex' | 'questionIds'>[],
): QuestionOutlineItem[] {
  const sortedGroups = [...groups].sort((left, right) => left.orderIndex - right.orderIndex)
  const groupedQuestionIds = new Set(sortedGroups.flatMap((group) => group.questionIds))
  const ungroupedQuestions = questions
    .filter((question) => !question.groupId && !groupedQuestionIds.has(question.id))
    .sort((left, right) => left.orderIndex - right.orderIndex)

  return [
    ...ungroupedQuestions.map((question) => ({
      kind: 'question' as const,
      questionId: question.id,
      groupInstanceId: null,
    })),
    ...sortedGroups.map((group) => ({
      kind: 'group' as const,
      groupInstanceId: group.id,
    })),
  ]
}

function completeQuestionOutline(
  outline: QuestionOutlineItem[],
  questions: Question[],
  groups: FormQuestionGroupInstanceContract[],
): QuestionOutlineItem[] {
  const existingQuestionIds = new Set(
    outline.flatMap((item) =>
      item.kind === 'question' && item.questionId ? [item.questionId] : [],
    ),
  )
  const existingGroupIds = new Set(
    outline
      .filter((item) => item.kind === 'group' && item.groupInstanceId)
      .map((item) => item.groupInstanceId as string),
  )
  const groupedQuestionIds = new Set(groups.flatMap((group) => group.questionIds))
  const missingQuestions = questions
    .filter(
      (question) =>
        !question.groupId &&
        !groupedQuestionIds.has(question.id) &&
        !existingQuestionIds.has(question.id),
    )
    .sort((left, right) => left.orderIndex - right.orderIndex)
    .map((question) => ({
      kind: 'question' as const,
      questionId: question.id,
      groupInstanceId: null,
    }))
  const missingGroups = groups
    .filter((group) => !existingGroupIds.has(group.id))
    .sort((left, right) => left.orderIndex - right.orderIndex)
    .map((group) => ({
      kind: 'group' as const,
      groupInstanceId: group.id,
    }))

  return [...outline, ...missingQuestions, ...missingGroups]
}

export function createInitialPersistentMockState(): PersistentMockState {
  const allForms = [...sampleForms]
  const formDetails = createInitialFormDetails()
  const questionGroupInstances = Object.fromEntries(
    Object.entries(formDetails).map(([formId, detail]) => [
      formId,
      normalizeFormGroupInstances(formId, detail.groups),
    ]),
  )
  const questionOutlineOrder = Object.fromEntries(
    Object.entries(formDetails).map(([formId, detail]) => [formId, detail.outline]),
  )

  const baseBusinessWorkflow = createDefaultBusinessWorkflowSnapshot()
  const mergedFieldMappings = baseBusinessWorkflow.fieldMappings
  const mergedFieldBindings = createFieldBindingsFromMappings(
    mergedFieldMappings,
    '2026-05-26T00:00:00.000Z',
  )

  return {
    version: 4,
    updatedAt: new Date().toISOString(),
    sourceMeta: createSyntheticMeta(),
    forms: allForms,
    formFolders: createInitialFolders(),
    formOrder: allForms.map((form) => form.id),
    deletedFormIds: [],
    responses: [],
    formDetails,
    questionGroupTemplates: createDefaultQuestionGroupTemplates(),
    questionGroupInstances,
    questionOutlineOrder,
    shareSettings: createInitialShareSettings(allForms),
    trackingLinks: {},
    emailShareTemplates: createInitialEmailShareTemplates(allForms),
    businessWorkflow: {
      ...baseBusinessWorkflow,
      applications: baseBusinessWorkflow.applications,
      customers: baseBusinessWorkflow.customers,
      fieldMappings: mergedFieldMappings,
      fieldBindings: mergedFieldBindings,
      sourceLinks: baseBusinessWorkflow.sourceLinks,
      fieldValues: baseBusinessWorkflow.fieldValues,
    },
  }
}

function mergeWithInitialState(value: unknown): PersistentMockState {
  const initial = createInitialPersistentMockState()
  if (!value || typeof value !== 'object') return initial
  const parsed = value as Partial<PersistentMockState>
  if (parsed.version !== 4) return initial

  const parsedBusinessWorkflow = (parsed.businessWorkflow ??
    {}) as Partial<BusinessWorkflowSnapshot>
  const mergedForms = mergeById(parsed.forms ?? initial.forms, initial.forms)
  const mergedFormFolders = mergeById(
    parsed.formFolders ?? initial.formFolders,
    initial.formFolders,
  )
  const parsedFormOrder = parsed.formOrder ?? initial.formOrder
  const mergedFormOrder = [
    ...parsedFormOrder,
    ...initial.formOrder.filter((formId) => !parsedFormOrder.includes(formId)),
  ]
  const parsedSalesUsers: SalesUser[] =
    parsedBusinessWorkflow.salesUsers ?? initial.businessWorkflow.salesUsers
  const mergedSalesUsers = normalizeSalesUsersForInternalSeed([
    ...parsedSalesUsers,
    ...initial.businessWorkflow.salesUsers.filter(
      (initialUser) => !parsedSalesUsers.some((user) => user.id === initialUser.id),
    ),
  ])
  const parsedTemplates = parsed.questionGroupTemplates ?? []
  const questionGroupTemplates = [
    ...parsedTemplates,
    ...initial.questionGroupTemplates.filter(
      (template) => !parsedTemplates.some((item) => item.id === template.id),
    ),
  ]
  const mergedFormDetails = Object.fromEntries(
    Object.entries({ ...initial.formDetails, ...(parsed.formDetails ?? {}) }).map(
      ([formId, detail]) => {
        const initialDetail = initial.formDetails[formId]
        const questions = mergeById(detail.questions ?? [], initialDetail?.questions ?? [])
        const groups = normalizeFormGroupInstances(
          formId,
          mergeById(
            (parsed.questionGroupInstances?.[formId] ??
              detail.groups ??
              []) as QuestionGroupUiContract[],
            (initial.questionGroupInstances[formId] ??
              initialDetail?.groups ??
              []) as QuestionGroupUiContract[],
          ),
        )
        const outline = completeQuestionOutline(
          detail.outline ??
            parsed.questionOutlineOrder?.[formId] ??
            createQuestionOutline(questions, groups),
          questions,
          groups,
        )

        return [
          formId,
          {
            questions,
            logicJumps: detail.logicJumps ?? [],
            hiddenFields: detail.hiddenFields ?? {},
            groups,
            outline,
          },
        ]
      },
    ),
  )
  const questionGroupInstances = Object.fromEntries(
    Object.entries(mergedFormDetails).map(([formId, detail]) => [formId, detail.groups]),
  )
  const questionOutlineOrder = Object.fromEntries(
    Object.entries(mergedFormDetails).map(([formId, detail]) => [formId, detail.outline]),
  )

  const parsedFieldMappings =
    parsedBusinessWorkflow.fieldMappings ?? initial.businessWorkflow.fieldMappings
  const mergedFieldMappings = refreshMiaolinPhoneFieldMappings(
    mergeById(parsedFieldMappings, initial.businessWorkflow.fieldMappings),
  )
  const parsedFieldBindings =
    parsedBusinessWorkflow.fieldBindings ??
    createFieldBindingsFromMappings(mergedFieldMappings, '2026-05-22T00:00:00.000Z')
  const mergedFieldBindings = refreshMiaolinPhoneFieldBindings([
    ...parsedFieldBindings,
    ...initial.businessWorkflow.fieldBindings.filter(
      (initialBinding) => !parsedFieldBindings.some((binding) => binding.id === initialBinding.id),
    ),
  ])
  const parsedResponses = parsed.responses ?? initial.responses
  const mergedResponses = parsedResponses.filter((response) => !isLegacyCustomerMockResponse(response))
  const parsedSourceLinks =
    parsedBusinessWorkflow.sourceLinks ?? initial.businessWorkflow.sourceLinks
  const mergedSourceLinks = parsedSourceLinks.filter(
    (link) => !legacyCustomerMockSourceLinkIds.has(link.id),
  )
  const parsedApplications =
    parsedBusinessWorkflow.applications ?? initial.businessWorkflow.applications
  const mergedApplications = mergeById(
    parsedApplications.filter(
      (application) => !legacyCustomerMockApplicationIds.has(application.id),
    ),
    initial.businessWorkflow.applications,
  )
  const parsedCustomers = parsedBusinessWorkflow.customers ?? initial.businessWorkflow.customers
  const mergedCustomers = isPreviewCustomerMockDataEnabled()
    ? mergeById(
        parsedCustomers.filter((customer) => !legacyCustomerMockCustomerIds.has(customer.id)),
        initial.businessWorkflow.customers,
      )
    : []

  return {
    ...initial,
    ...parsed,
    sourceMeta: createSyntheticMeta(),
    forms: mergedForms,
    formFolders: mergedFormFolders,
    formOrder: mergedFormOrder,
    deletedFormIds: parsed.deletedFormIds ?? initial.deletedFormIds,
    responses: mergedResponses,
    formDetails: mergedFormDetails,
    questionGroupTemplates,
    questionGroupInstances,
    questionOutlineOrder,
    shareSettings: { ...initial.shareSettings, ...(parsed.shareSettings ?? {}) },
    trackingLinks: parsed.trackingLinks ?? initial.trackingLinks,
    emailShareTemplates: {
      ...initial.emailShareTemplates,
      ...(parsed.emailShareTemplates ?? {}),
    },
    businessWorkflow: {
      ...initial.businessWorkflow,
      ...parsedBusinessWorkflow,
      salesUsers: mergedSalesUsers,
      applications: mergedApplications,
      customers: mergedCustomers,
      fieldMappings: mergedFieldMappings,
      fieldBindings: mergedFieldBindings,
      sourceLinks: mergedSourceLinks,
      fieldValues: refreshMiaolinPhoneFieldValues({
        ...initial.businessWorkflow.fieldValues,
        ...(parsedBusinessWorkflow.fieldValues ?? {}),
      }),
    },
  }
}

function isLegacyCustomerMockResponse(response: ResponseSummary) {
  if (legacyCustomerMockResponseIds.has(response.id)) return true
  if (response.formId !== primaryFormId) return false
  const source = String(response.metadata?.source ?? '')
  return source === 'miaolin-new-customer-csv-demo'
}

function writeState(nextState: PersistentMockState) {
  const storage = getStorage()
  if (!storage) {
    memoryState = clone(nextState)
    return
  }
  storage.setItem(getPersistentMockStorageKey(), JSON.stringify(nextState))
}

export function loadFixture(): PersistentMockState {
  const storage = getStorage()
  if (!storage) {
    memoryState = mergeWithInitialState(memoryState)
    return clone(memoryState)
  }

  const storageKey = getPersistentMockStorageKey()
  const raw = storage.getItem(storageKey)
  if (!raw) {
    const legacyRaw =
      storageKey !== PERSISTENT_MOCK_STORAGE_KEY
        ? storage.getItem(PERSISTENT_MOCK_STORAGE_KEY)
        : null
    if (legacyRaw) {
      try {
        const migrated = mergeWithInitialState(JSON.parse(legacyRaw))
        writeState(migrated)
        return clone(migrated)
      } catch {
        // Fall through to fresh state for this demo identity.
      }
    }
    const initial = createInitialPersistentMockState()
    writeState(initial)
    return clone(initial)
  }

  try {
    const merged = mergeWithInitialState(JSON.parse(raw))
    writeState(merged)
    return clone(merged)
  } catch {
    const initial = createInitialPersistentMockState()
    writeState(initial)
    return clone(initial)
  }
}

export function savePatch(
  patch:
    | Partial<PersistentMockState>
    | ((current: PersistentMockState) => Partial<PersistentMockState>),
): PersistentMockState {
  const current = loadFixture()
  const resolvedPatch = typeof patch === 'function' ? patch(current) : patch
  const nextState: PersistentMockState = {
    ...current,
    ...resolvedPatch,
    version: 4,
    updatedAt: new Date().toISOString(),
    sourceMeta: createSyntheticMeta(),
  }
  writeState(nextState)
  return clone(nextState)
}

export function resetFixture(): PersistentMockState {
  const initial = createInitialPersistentMockState()
  writeState(initial)
  return clone(initial)
}

export function exportSnapshot(): PersistentMockState {
  return loadFixture()
}

export function saveBusinessWorkflowSnapshot(snapshot: BusinessWorkflowSnapshot) {
  return savePatch({ businessWorkflow: snapshot }).businessWorkflow
}

export function saveFormDetailSnapshot(formId: string, detail: PersistentFormDetailSnapshot) {
  const groups = normalizeFormGroupInstances(formId, detail.groups)
  const outline = detail.outline ?? createQuestionOutline(detail.questions, groups)
  return savePatch((current) => ({
    formDetails: {
      ...current.formDetails,
      [formId]: {
        ...detail,
        groups,
        outline,
      },
    },
    questionGroupInstances: {
      ...current.questionGroupInstances,
      [formId]: groups,
    },
    questionOutlineOrder: {
      ...current.questionOutlineOrder,
      [formId]: outline,
    },
  }))
}

export function saveShareSettingsSnapshot(settings: ShareSettingsContract) {
  return savePatch((current) => ({
    shareSettings: {
      ...current.shareSettings,
      [settings.formId]: settings,
    },
  })).shareSettings[settings.formId]
}

export function saveTrackingLinkSnapshot(link: TrackingLinkContract) {
  return savePatch((current) => ({
    trackingLinks: {
      ...current.trackingLinks,
      [link.formId]: [link, ...(current.trackingLinks[link.formId] ?? [])],
    },
  })).trackingLinks[link.formId]
}

export function saveEmailShareTemplateSnapshot(
  formId: string,
  template: EmailShareTemplateContract,
) {
  return savePatch((current) => {
    const templates = current.emailShareTemplates[formId] ?? []
    const nextTemplates = templates.some((item) => item.id === template.id)
      ? templates.map((item) => (item.id === template.id ? template : item))
      : [template, ...templates]
    return {
      emailShareTemplates: {
        ...current.emailShareTemplates,
        [formId]: nextTemplates,
      },
    }
  }).emailShareTemplates[formId]
}

export function deleteEmailShareTemplateSnapshot(formId: string, templateId: string) {
  return savePatch((current) => {
    const templates = current.emailShareTemplates[formId] ?? []
    return {
      emailShareTemplates: {
        ...current.emailShareTemplates,
        [formId]: templates.filter((template) => template.id !== templateId),
      },
    }
  }).emailShareTemplates[formId]
}
